import { Product } from '@features/catalog/models/product.models';
import {
  AttributeGroupDraft,
  AttributeValueDraft,
} from '@features/catalog/models/product-form-draft.models';
import { filterActiveCatalogAttributeValues } from '@features/catalog/utils/catalog-attribute-value-active.util';

function collectEnabledValueIds(product: Product): Set<string> {
  return new Set(
    (product.enabledAttributeValueIds ?? [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  );
}

function shouldIncludeCatalogValue(
  valueId: string,
  enabledValueIds: Set<string>,
  hasExplicitEnablements: boolean
): boolean {
  if (!hasExplicitEnablements) {
    return false;
  }

  return enabledValueIds.has(valueId);
}

export function buildAttributeGroupsFromProduct(
  product: Product,
  nextTempId: () => string
): AttributeGroupDraft[] {
  const enabledValueIds = collectEnabledValueIds(product);
  const hasExplicitEnablements = enabledValueIds.size > 0;

  const groupsByDefinitionId = new Map<
    string,
    {
      name: string;
      catalogDefinitionActive: boolean;
      values: Map<string, AttributeValueDraft>;
    }
  >();

  const ensureGroup = (
    definitionId: string,
    definitionName: string,
    catalogDefinitionActive: boolean
  ) => {
    const existing = groupsByDefinitionId.get(definitionId);
    if (existing) {
      return existing;
    }

    const created = {
      name: definitionName,
      catalogDefinitionActive,
      values: new Map<string, AttributeValueDraft>(),
    };
    groupsByDefinitionId.set(definitionId, created);
    return created;
  };

  (product.productVariants ?? []).forEach((variant) => {
    (variant.productAttributeValues ?? []).forEach((attributeValue) => {
      const definitionId = attributeValue.productAttributeDefinition?.id?.trim() ?? '';
      const definitionName = attributeValue.productAttributeDefinition?.name?.trim() ?? '';
      const definitionActive = attributeValue.productAttributeDefinition?.active ?? true;
      const valueId = String(attributeValue.id ?? '').trim();
      const value = attributeValue.value?.trim() ?? '';

      if (!definitionId || !definitionName || !valueId || !value) {
        return;
      }

      const group = ensureGroup(definitionId, definitionName, definitionActive);
      if (!group.values.has(valueId)) {
        group.values.set(valueId, {
          tempId: nextTempId(),
          id: valueId,
          value,
        });
      }
    });
  });

  const knownDefinitionIds = new Set(product.productAttributeDefinitions.map((definition) => definition.id));

  const groupsFromDefinitions: AttributeGroupDraft[] = product.productAttributeDefinitions.map((definition) => {
    const mergedValues = new Map<string, AttributeValueDraft>();

    filterActiveCatalogAttributeValues(definition.values).forEach((value) => {
      const valueId = String(value.id ?? '').trim();
      const valueText = value.value?.trim() ?? '';

      if (!valueId || !valueText) {
        return;
      }

      if (!shouldIncludeCatalogValue(valueId, enabledValueIds, hasExplicitEnablements)) {
        return;
      }

      mergedValues.set(valueId, {
        tempId: nextTempId(),
        id: valueId,
        value: valueText,
      });
    });

    Array.from(groupsByDefinitionId.get(definition.id)?.values.values() ?? []).forEach((value) => {
      const valueId = value.id?.trim() ?? '';
      if (!valueId || !value.value.trim()) {
        return;
      }
      mergedValues.set(valueId, {
        tempId: nextTempId(),
        id: valueId,
        value: value.value,
      });
    });

    const values = Array.from(mergedValues.values());

    return {
      tempId: nextTempId(),
      definitionId: definition.id,
      name: definition.name,
      values,
      usedForVariations: values.length > 0,
      catalogDefinitionActive: definition.active ?? true,
    };
  });

  const groupsFromVariants: AttributeGroupDraft[] = Array.from(groupsByDefinitionId.entries())
    .filter(([definitionId]) => !knownDefinitionIds.has(definitionId))
    .map(([definitionId, group]) => ({
      tempId: nextTempId(),
      definitionId,
      name: group.name,
      values: Array.from(group.values.values()).map((value) => ({
        tempId: nextTempId(),
        id: value.id,
        value: value.value,
      })),
      usedForVariations: group.values.size > 0,
      catalogDefinitionActive: group.catalogDefinitionActive,
    }));

  return [...groupsFromDefinitions, ...groupsFromVariants];
}
